import { openNewTab } from '@siteground/styleguide';
import { push } from 'redux-first-history';
import { call, debounce, delay, fork, put, race, select, takeEvery, takeLatest, takeLeading } from 'redux-saga/effects';
import CONFIG from '../../../config';
import { createSystemMessage } from '../../actions';
import { removeFileForUpload } from '../../actions/file-upload';
import { CHAT_STATE, SYSTEM_MESSAGE_TYPES } from '../../constants';
import { ROUTES } from '../../constants/routes';
import { getChatState, getIsChatMessagingInProgress } from '../../selectors/chat';
import { getPendingFileForUpload } from '../../selectors/file-upload';
import { getOperatorInfo } from '../../selectors/operator';
import { getSalesPromotionParams } from '../../selectors/sales-promotion';
import { getRequestId } from '../../selectors/chat';
import { getVisitorToken } from '../../selectors/chat-session';
import { encodeMessage } from '../../utils/encode-decode-unicode';
import { sendToCluster } from '../api/websocket';
import { notifyClientTypyingState } from '../../../../src/actions/client';

import * as FromClientFrontendActions from '../actions/from-client-frontend';
import {
  CHAT_CLOSED_BY_CLIENT,
  CHAT_WINDOW_RENDERED,
  CLIENT_IS_TYPING,
  CLOSE_CHAT_UI_WINDOW,
  FAILED_TO_LOAD_VISITOR_TOKEN,
  INIT_WEBSOCKET,
  INIT_WEBSOCKET_UNAUTHORIZED,
  REQUEST_LOGIN_PRESALE,
  REQUEST_START_CHAT_CANCELLATION,
  REQUEST_START_CHAT_TECHNICAL,
  SEND_CHAT_FEEDBACK,
  CHAT_FEEDBACK_SUCCESS,
  SEND_CHAT_INQUIRY,
  SEND_CHAT_MESSAGE,
  SEND_CHAT_TRANSCRIPT,
  SEND_OPERATOR_FEEDBACK,
  SEND_USER_TO_THIRD_PARTY_FOR_REVIEW,
  sendChatMessageFailed,
  sendChatMessagePending,
  sendChatMessageSucceeded,
  setChatStatus,
  storeSessionInfo,
  chatFeedbackSuccess,
  SUBMIT_FILES_FOR_UPLOAD
} from '../actions/from-client-frontend';

import { userSubmittedChatRating } from '../../../../src/actions/chat';
import { CHAT_NOT_EXISTS } from '../actions/from-cluster';
// utils, api calls, system functions
import { recaptchaPromise } from '../actions/recaptcha';

import { TOGGLE_CHAT_SOUND } from '../../constants';
import { userToggledSound } from '../../../../src/actions/chat';

import {
  closeChatBeforeAccepted,
  endChat,
  loginPresale,
  recordFeedbackClick,
  requestStartChat,
  sendChatFeedback,
  sendChatMessage,
  sendChatTranscript,
  sendFeedback,
  submitReviewToThirdParty,
  uploadFile
} from '../api/chat-api';
import { initWebsocket } from '../api/websocket';
import AppEvents from '../constants/events';
import EVENTS from '../constants/events';
import { trigger } from '../events-manager';
import { generateGETParams } from '../utils/request-params';

import { closeChatWindowSaga } from './chat-window-helpers';
import { watchClusterEventsChannel } from './from-cluster-handlers';

import { onMessageConsistencyCheck } from './messages-consistency-check';
import { handleVisitorTokenRequest, waitForClusterAuthentication } from './visitor-token-helpers';

import { debugEvent } from '../../actions/debug';
import { getJwtBodyPart } from '../utils/jwt';
import { store } from '../../index';
import { chatSessionReducer } from '../../reducers/chat-session';

const VISITOR_TYPING_MIN_TIMEOUT = 5000;
const ACTIONS_TRIGGERING_MESSAGE_CONSISTENCY_CHECK = [CHAT_CLOSED_BY_CLIENT, CHAT_NOT_EXISTS];

function* onRequestStartChatTechnical({ action, visitorToken, CHAT_API }) {
  const { payload } = action;
  const state = store.getState();
  const getParams = generateGETParams({ _visitor_token: visitorToken });

  yield put(
    debugEvent({
      name: 'onRequestStartChatTechnical',
      payload: {
        visitorToken: getJwtBodyPart({ token: visitorToken })
      }
    })
  );

  yield put(setChatStatus(CHAT_STATE.REQUESTING_CHAT));

  const body = {
    ...payload
  };

  if (state.chatSession.aiAssistantThreadId) {
    body.thread_id = state.chatSession.aiAssistantThreadId;
  }

  return yield call(requestStartChat, { CHAT_API, getParams }, body);
}

function* onRequestLoginPresales({ action, CHAT_API }) {
  const { payload } = action;

  const recaptchaToken3 = yield recaptchaPromise(CONFIG.RECAPTCHA_TOKEN);

  const salesParams = yield select(getSalesPromotionParams);

  // TODO handle case when there are no available operators
  const response = yield call(
    loginPresale,
    { CHAT_API },
    {
      ...payload,
      ...salesParams,
      recaptcha_token: recaptchaToken3
    }
  );
  const visitorToken = response && response.data && response.data.token; // TODO add fail

  yield put(FromClientFrontendActions.storeVisitorToken({ visitorToken }));

  yield fork(waitForClusterAuthentication, {
    onFailedAuthentication: function* (err) {
      yield put(FromClientFrontendActions.setChatStatus(CHAT_STATE.FAILED_AUTHENTICATION_WITH_CLUSTER));
    }
  });

  yield put(FromClientFrontendActions.initWebsocketUnAuthorized());

  yield put(setChatStatus(CHAT_STATE.REQUESTING_CHAT));

  trigger(AppEvents.ON_AFTER_CHAT_STARTED);

  return response;
}

function* onSendChatMessage({ action, visitorToken, CHAT_API }) {
  const { message } = action && action.payload;
  const timestamp = +new Date();
  const getParams = generateGETParams({ _visitor_token: visitorToken });

  yield put(sendChatMessagePending({ message, fe_ts: timestamp }));

  try {
    const { response, timeout } = yield race({
      response: call(
        sendChatMessage,
        { CHAT_API, getParams },
        {
          ...action.payload,
          message: encodeMessage(message),
          fe_ts: timestamp
        }
      ),
      timeout: delay(10000)
    });

    if (timeout) {
      return yield put(sendChatMessageFailed({ fe_ts: timestamp }));
    }
    // const response = yield call(sendChatMessage, { CHAT_API, getParams }, { message: encodeMessage(message) });
    const id = response && response.data && response.data.id;

    yield put(sendChatMessageSucceeded({ id, fe_ts: timestamp }));

    return response;
  } catch (e) {
    console.warn(e);
    return yield put(sendChatMessageFailed({ fe_ts: timestamp }));
  }
}

function* onClientIsTyping() {
  const requestId = yield select(getRequestId);
  sendToCluster(notifyClientTypyingState({ requestId, isTypying: true }));
  yield delay(VISITOR_TYPING_MIN_TIMEOUT);
}

function* onClientIsTypingStopped() {
  const requestId = yield select(getRequestId);
  sendToCluster(notifyClientTypyingState({ requestId, isTypying: false }));
}

function* onSendFeedbackForOperator({ action, visitorToken, CHAT_API }) {
  const payload = action && action.payload;
  const getParams = generateGETParams({ _visitor_token: visitorToken });
  return yield call(sendFeedback, { CHAT_API, getParams }, payload);
}

function* onSendFeedbackForChat({ action, visitorToken, CHAT_API }) {
  const payload = action && action.payload;
  const getParams = generateGETParams({ _visitor_token: visitorToken });
  const response = yield call(sendChatFeedback, { CHAT_API, getParams }, payload);

  yield put(chatFeedbackSuccess(response.data));
  return response;
}

function* onSendChatTranscript({ action, visitorToken, CHAT_API }) {
  const payload = action && action.payload;
  const getParams = generateGETParams({ _visitor_token: visitorToken });
  return yield call(sendChatTranscript, { CHAT_API, getParams }, payload);
}

function* onSubmitReviewToThirdParty({ action, visitorToken, CHAT_API }) {
  const { payload } = action;
  const { entityIdentificator, clickValue } = payload;

  const getParams = generateGETParams({ _visitor_token: visitorToken });
  const response = yield call(submitReviewToThirdParty, { CHAT_API, getParams, payload });

  openNewTab({
    url: response.data.reviewHref,
    target: '_blank'
  });

  return yield call(
    recordFeedbackClick,
    { CHAT_API, getParams },
    {
      entity_identificator: entityIdentificator,
      value: clickValue
    }
  );
}

function* onInitWebsocketUnAuthorized() {
  const { chatSession } = yield select();
  const visitorToken = yield select(getVisitorToken);

  const clusterEventsChannel = yield call(initWebsocket, {
    IMPULSE_URLS: chatSession && chatSession.IMPULSE_URLS,
    visitorToken,
    unauthorized: true
  });

  // run watcher for events from cluster
  yield fork(watchClusterEventsChannel, { clusterEventsChannel, connectedVisitorToken: visitorToken });
}

function* onInitWebsocket(initWebsocketAction) {
  const { payload } = initWebsocketAction;
  const visitorToken = yield select(getVisitorToken);

  const { IMPULSE_URLS, CHAT_API, clientToken, username, firstName, lastName, language, reconnect = false } = payload;

  const clusterEventsChannel = yield call(initWebsocket, {
    IMPULSE_URLS,
    token: clientToken,
    username,
    // initially sending visitor token from local storage
    visitorToken
  });

  if (!reconnect) {
    yield put(
      storeSessionInfo({
        IMPULSE_URLS,
        CHAT_API,
        clientToken,
        username,
        firstName,
        lastName,
        language
      })
    );

    // event for main app to know everything is ready to start chats/other communication
    trigger(EVENTS.REALTIME_CLIENT_INITIALIZED);
  }

  // run watcher for events from cluster
  yield fork(watchClusterEventsChannel, { clusterEventsChannel, connectedVisitorToken: visitorToken });
}

function* onChatWindowRender() {
  trigger(AppEvents.ON_AFTER_CHAT_RENDER);
}

function* onSubmitFileForUpload({ action, visitorToken, CHAT_API }) {
  const getParams = generateGETParams({ _visitor_token: visitorToken });
  const fileObject = yield select(getPendingFileForUpload);

  if (!fileObject) {
    return;
  }

  yield put(removeFileForUpload());

  const response = yield call(uploadFile, { CHAT_API, getParams }, { file: fileObject.file });

  return response;
}

function* onCloseChatSaga({ action, visitorToken, CHAT_API }) {
  const getParams = generateGETParams({ _visitor_token: visitorToken });
  const chatState = yield select(getChatState);
  const chatStateIsRequesting = chatState === CHAT_STATE.REQUESTING_CHAT;
  const chatStateInProgress = yield select(getIsChatMessagingInProgress);
  const operator = yield select(getOperatorInfo);
  const operatorName = `${operator.first_name} ${operator.last_name}`;

  switch (true) {
    case chatStateIsRequesting:
      yield call(closeChatBeforeAccepted, { CHAT_API, getParams });
      yield call(closeChatWindowSaga);
      break;
    case chatStateInProgress:
      yield call(endChat, { CHAT_API, getParams });
      yield put(setChatStatus(CHAT_STATE.ENDED_BY_USER));
      yield put(push(ROUTES.CHAT_END));
      yield put(
        createSystemMessage({
          type: SYSTEM_MESSAGE_TYPES.INSTRUCTION,
          intlKey: 'translate.system.message.chat.session.ended.by.client',
          intlValues: { operatorName }
        })
      );
      break;
    default:
      yield call(closeChatWindowSaga);
  }
}

function* handleFailedToLoadVisitorToken() {
  trigger(EVENTS.FAILED_TO_LOAD_VISITOR_TOKEN);
  yield call(closeChatWindowSaga);
}

function onToggleChatSound({ payload }) {
  const { isSoundOn, isDistributed } = payload;

  if (!isDistributed) {
    sendToCluster(userToggledSound({ isSoundOn }));
  }
}

function* onChatInquiry(action) {
  const inquiry = action.payload.inquiry;
  trigger(AppEvents.ON_INQUIRY_CHAT_SUBMIT, { inquiry });
}

export default function* () {
  yield takeLatest(INIT_WEBSOCKET, onInitWebsocket);
  yield takeLatest(INIT_WEBSOCKET_UNAUTHORIZED, onInitWebsocketUnAuthorized);
  yield takeLatest(CHAT_WINDOW_RENDERED, onChatWindowRender);
  yield takeLatest(SEND_CHAT_INQUIRY, onChatInquiry);

  yield takeLatest(REQUEST_LOGIN_PRESALE, handleVisitorTokenRequest(onRequestLoginPresales));
  yield takeLatest(REQUEST_START_CHAT_TECHNICAL, handleVisitorTokenRequest(onRequestStartChatTechnical));
  yield takeLatest(REQUEST_START_CHAT_CANCELLATION, handleVisitorTokenRequest(onRequestStartChatTechnical));

  yield takeEvery(SEND_CHAT_MESSAGE, handleVisitorTokenRequest(onSendChatMessage));

  // User typing watchers
  yield takeLeading(CLIENT_IS_TYPING, onClientIsTyping);
  yield debounce(VISITOR_TYPING_MIN_TIMEOUT, CLIENT_IS_TYPING, onClientIsTypingStopped);

  yield takeLatest(SEND_OPERATOR_FEEDBACK, handleVisitorTokenRequest(onSendFeedbackForOperator));

  yield takeLatest(
    SEND_CHAT_FEEDBACK,
    handleVisitorTokenRequest(onSendFeedbackForChat, function* onRequestSucceeded() {
      sendToCluster(userSubmittedChatRating());
    })
  );

  yield takeLatest(SEND_CHAT_TRANSCRIPT, handleVisitorTokenRequest(onSendChatTranscript));
  yield takeLatest(SEND_USER_TO_THIRD_PARTY_FOR_REVIEW, handleVisitorTokenRequest(onSubmitReviewToThirdParty));
  yield takeLatest(SUBMIT_FILES_FOR_UPLOAD, handleVisitorTokenRequest(onSubmitFileForUpload));
  yield takeLatest(CHAT_CLOSED_BY_CLIENT, handleVisitorTokenRequest(onCloseChatSaga));
  yield takeLatest(FAILED_TO_LOAD_VISITOR_TOKEN, handleFailedToLoadVisitorToken);

  yield takeLatest(CLOSE_CHAT_UI_WINDOW, closeChatWindowSaga);
  yield takeEvery(TOGGLE_CHAT_SOUND, onToggleChatSound);

  // Consistency checks
  yield takeLeading(ACTIONS_TRIGGERING_MESSAGE_CONSISTENCY_CHECK, handleVisitorTokenRequest(onMessageConsistencyCheck));
}
